import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Collapse,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  UncontrolledPopover,
  PopoverBody,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Label,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import EnrolledStudentsTab from "./EnrolledStudentsTab";
import Assessment from "./Assessment";
import ReviewFeedback from "pages/ReviewFeedback/ReviewFeedback";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import CourseSchedule from "./CourseSchedule";
import {
  CONTENT_DELIVERY_MODE_LABELS,
  PARTICIPANT_TYPE_LABELS,
  COURSE_STATUS_OPTIONS
} from "components/constant";
import { getCourseById } from "action/course";
import { toTitleCase, formattedPrice, formatText} from "components/CommonComponents/CommonFunctions";
import { getCourseStatusColor, PREVIEW_LIMIT, LessonTypeColor, OFFICE_PREVIEW_URL} from "constants/CommonConstants";


import { ThumbnailPreview, mapBackendTemplateToFrontend } from "components/CommonComponents/CertificatePreview";


const DEFAULT_COURSE = {
  courseName: { value: "-" },
  category: { value: "-" },
  level: { value: "-" },
  courseLanguage: { value: "-" },
  courseDescription: { value: "" },

  courseStartDate: { value: "-" },
  courseEndDate: { value: "-" },
  courseRating: { value: 0 },

  preRequisites: { value: "NO" },
  requiredDoc: { value: [] },

  certificateEnabled: { value: "NO" },

  courseMedia: { value: null },

  TrainerName: { value: "-" },

  courseType: { value: "-" },
  courseStatus: {
    value: "-",
  },
  accessType: { value: "-" },
  price: { value: "-" },

  maxParticipants: { value: "-" },
  offlineAddress: { value: "-" },
  totalEnrollment: { value: "-" },

  courseUsers: { value: "-" },
  payingUserType: { value: "-" },
  contentDeliveryMode: { value: "-" },
  // assessmentType: { value: "-" },
  batches: [],
  lessonByLanguage: {
    englishLessons: [],
    hindiLessons: [],
  },
};

const SectionHeader = ({ title, isOpen, handleOnClick }) => (
  <div className="d-flex  align-items-center cursor" onClick={handleOnClick}>
    <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
    <h5 className="ml-2 mb-0 configurations-header-title">{title}</h5>
  </div>
);

function CourseDetail() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "COURSE";
  const [activeTab, setActiveTab] = useState("details");
  const [openLessonIndex, setOpenLessonIndex] = useState(0);
  const [courseData, setCourseData] = useState(DEFAULT_COURSE);

  const [activeLessonLanguage, setActiveLessonLanguage] = useState("ENGLISH");
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [selectedLessonDocument, setSelectedLessonDocument] = useState({
    url: "",
    type: "",
  });
  const [isLessonDocumentModalOpen, setIsLessonDocumentModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState({
    loader: false,
    error: "",
  });
  const [activeCertTab, setActiveCertTab] = useState("completion");
  const [isThumbnailModalOpen, setIsThumbnailModalOpen] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.35);
  const [modalScale, setModalScale] = useState(1);
  const modalObserverRef = React.useRef(null);
  const previewObserverRef = React.useRef(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [isCourseDescExpanded, setIsCourseDescExpanded] = useState(false);

  const setPreviewRef = React.useCallback((node) => {
    if (previewObserverRef.current) {
      previewObserverRef.current.disconnect();
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width) {
            setPreviewScale(entry.contentRect.width / 900);
          }
        }
      });
      observer.observe(node);
      previewObserverRef.current = observer;
    }
  }, []);

  const setModalRef = React.useCallback((node) => {
    if (modalObserverRef.current) {
      modalObserverRef.current.disconnect();
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width) {
            setModalScale(entry.contentRect.width / 900);
          }
        }
      });
      observer.observe(node);
      modalObserverRef.current = observer;
    }
  }, []);

  const toggleLessonDocumentModal = () =>
    setIsLessonDocumentModalOpen((prev) => !prev);

  //decode id here from url
  const decodedId = id ? urlDecodedId(id) : null;

  useEffect(() => {
    if (!decodedId) {
      return;
    }

    fetchCourseById(decodedId);
  }, [decodedId]);

  const toggleCertificateModal = () =>
    setIsCertificateModalOpen((prev) => !prev);

  const toggleThumbnailModal = () => setIsThumbnailModalOpen((prev) => !prev);

  const fetchCourseById = async (courseId) => {
    try {
      setIsLoading({
        loader: true,
        error: "",
      });
      const res = await getCourseById(courseId);

      if (res?.code !== 200) {
        setIsLoading({
          loader: false,
          error: res?.message || "Failed to fetch course details.",
        });
        return;
      }

      const course = res.data;

      setCourseData((prev) => ({
        ...prev,
        courseName: {
          value: course.courseName || "-",
        },

        category: {
          value: course.courseCategoryName || "-",
        },

        level: {
          value: toTitleCase(course.level),
        },

        courseLanguage: {
          value: toTitleCase(course.courseLanguage),
        },

        courseDescription: {
          value: course.courseDescription,
        },

        accessType: {
          value: toTitleCase(course.accessType),
        },

        price: {
          value: course.coursePrice,
        },

        courseRating: {
          value: Number(course.averageRating || 0),
        },

        courseStartDate: {
          value: course.totalCourseDuration || 0,
        },

        totalEnrollment: {
          value: course.totalEnrolledUsers || 0,
        },

        courseType: {
          value: toTitleCase(course.courseType),
        },

        courseStatus: {
          value: course.courseStatus,
        },

        preRequisites: {
          value: course.requiredPrerequisiteDocuments ? "YES" : "NO",
        },

        courseUsers: {
          value: PARTICIPANT_TYPE_LABELS[course.courseUsers],
        },

        payingUserType: {
          value: toTitleCase(course.payingUserType),
        },

        contentDeliveryMode: {
          value: CONTENT_DELIVERY_MODE_LABELS[course.contentDeliveryMode],
        },

        // assessmentType: {
        //   value: ASSESSMENT_TYPE_LABELS[course.assessmentType],
        // },

        requiredDoc: {
          value:
            course.coursePrerequisiteDocument?.map((doc) => ({
              value: doc.courseDocumentID,
              label: doc.courseDocumentName,
            })) || [],
        },

        certificateEnabled: {
          value: (
            course.enableCeritificate !== undefined
              ? course.enableCeritificate
              : course.enableCertificate
          )
            ? "YES"
            : "NO",
        },

        certificateDetails: {
          value: course.certificateTemplates || [],
        },

        lessonByLanguage: {
          englishLessons: (course.lessonByLanguage?.englishLessons || []).map(
            (lesson) => ({
              id: lesson.id,
              lessonSeqNo: lesson.lesson_seq_no,
              lessonName: lesson.lesson_name,
              type: lesson.lesson_document_type,
              duration: `${lesson.duration} min`,
              description: lesson.description,
              language: lesson.language,
              documents: lesson.documents || [],
            }),
          ),
          hindiLessons: (course.lessonByLanguage?.hindiLessons || []).map(
            (lesson) => ({
              id: lesson.id,
              lessonSeqNo: lesson.lesson_seq_no,
              lessonName: lesson.lesson_name,
              type: lesson.lesson_document_type,
              duration: `${lesson.duration} min`,
              description: lesson.description,
              language: lesson.language,
              documents: lesson.documents || [],
            }),
          ),
        },

        courseMedia: {
          value: course.thumbnailPath,
        },

        batches:
          course.batches?.map((batch) => ({
            id: batch.ID,
            code: batch.code,
            name: batch.name,
            trainers:
              batch.trainers?.map((trainer) =>
                [trainer.user?.firstName, trainer.user?.lastName]
                  .filter(Boolean)
                  .join(" "),
              ) || [],
            registrationStartDate: batch.registrationStartDate,
            registrationEndDate: batch.registrationEndDate,
            batchStartDate: batch.batchStartDate,
            batchEndDate: batch.batchEndDate,
            startTime: batch.startTime,
            endTime: batch.endTime,
            enrollmentCount: batch.enrolledUserCount,
          })) || [],
      }));
    } catch (error) {
      setIsLoading({
        loader: false,
        error:
          error?.message ||
          "Something went wrong while fetching course details.",
      });
    } finally {
      setIsLoading({
        loader: false,
        error: "",
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setActiveTab(params.get("tab") || "details");
  }, [location.search]);

  const getLessonIcon = (lesson) => {
    const filePath = lesson.documents?.[0]?.file_path || "";

    const extension = filePath.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "webp":
      case "svg":
      case "avif":
        return "fa-image";

      case "pdf":
        return "fa-file-pdf";

      case "doc":
      case "docx":
        return "fa-file-word";

      case "xls":
      case "xlsx":
        return "fa-file-excel";

      case "mp4":
      case "mov":
      case "webm":
      case "m4v":
        return "fa-video";

      default:
        return "fa-file";
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);

      if (tab === "assessment") {
        params.set("lang", derivedCourseLanguage);
      }
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
        state: { mode },
      });
    }
  };

  const derivedCourseLanguage = courseData.courseLanguage?.value || "-";

  const displayedLessons =
    activeLessonLanguage === "HINDI"
      ? courseData.lessonByLanguage?.hindiLessons || []
      : courseData.lessonByLanguage?.englishLessons || [];

  const hasLessonLanguageTabs =
    (courseData.lessonByLanguage?.englishLessons?.length || 0) > 0 &&
    (courseData.lessonByLanguage?.hindiLessons?.length || 0) > 0;

  const totalLessons =
    (courseData.lessonByLanguage?.englishLessons?.length || 0) +
    (courseData.lessonByLanguage?.hindiLessons?.length || 0);

  const isPaid = courseData.accessType?.value === "Paid";

  const isLiveCourse = courseData.courseType?.value === "Live";

  // const isOfflineOrHybrid =
  //   courseData.contentDeliveryMode?.value === "Offline" ||
  //   courseData.contentDeliveryMode?.value === "Online, Offline";

  const isOffline = courseData.contentDeliveryMode?.value === "Offline"; 

  const hasConfigurations =
    courseData.courseUsers?.value ||
    courseData.courseLanguage?.value ||
    courseData.accessType?.value ||
    (isPaid && courseData.payingUserType?.value) ||
    (isPaid && courseData.price?.value) ||
    courseData.courseType?.value ||
    (isLiveCourse && courseData.contentDeliveryMode?.value);
    // || (isOfflineOrHybrid && courseData.assessmentType?.value);

  const mediaUrl = courseData.courseMedia?.value || "";
  const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(mediaUrl);

  const getCourseListingRoute = (currentRole) => {
    switch (currentRole) {
      case "SUPER_ADMIN":
        return "/super-admin/course";

      case "ADMIN":
        return "/admin/course";

      case "CHANNEL_PARTNER":
        return "/channel-partner/course";

      case "TRAINER":
        return "/instructor/course";

      default:
        return "/admin/course";
    }
  };

  // const truncateHtml = (html, limit) => {
  //   // Create a temporary container to safely parse the HTML string
  //   const container = document.createElement("div");
  //   container.innerHTML = html;

  //   // Tracks the total number of text characters processed
  //   let count = 0;

  //   // Recursively traverse all nodes in the HTML
  //   const walk = (node) => {
  //     // Remove any remaining nodes once the character limit is reached
  //     if (count >= limit) {
  //       node.parentNode?.removeChild(node);
  //       return;
  //     }

  //     // Process only text nodes for character counting
  //     if (node.nodeType === Node.TEXT_NODE) {
  //       const remaining = limit - count;

  //       // Truncate the current text node if it exceeds the remaining limit
  //       if (node.textContent.length > remaining) {
  //         node.textContent = node.textContent.slice(0, remaining) + "...";
  //         count = limit;
  //       } else {
  //         // Otherwise, count the entire text node
  //         count += node.textContent.length;
  //       }
  //     }
  //     // Recursively process child nodes while preserving HTML structure
  //     else if (node.nodeType === Node.ELEMENT_NODE) {
  //       Array.from(node.childNodes).forEach(walk);
  //     }
  //   };

  //   // Start traversing from the root-level child nodes
  //   Array.from(container.childNodes).forEach(walk);

  //   // Return the truncated HTML with its original formatting preserved
  //   return container.innerHTML;
  // };

    const renderExpandableDescription = (
    html,
    isExpanded,
    onToggle,
    className = "course-content",
  ) => {
    // 1. Sanitize the HTML first
    const sanitized = DOMPurify.sanitize(
      html || "<p>No description available.</p>",
    );

    // 2. Strip HTML tags to get plain text
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitized;
    // Replace block elements with newlines to preserve some structure
    tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6, p, br, li').forEach(el => {
        el.insertAdjacentText('afterend', '\n');
    });
    const plainText = tempDiv.textContent || tempDiv.innerText || "";

    const isLong = plainText.length > PREVIEW_LIMIT;

    return (
      <>
        <div
          className={className}
          style={{ whiteSpace: "pre-wrap" }} // Preserves the newlines
        >
          {isExpanded || !isLong
            ? plainText
            : plainText.slice(0, PREVIEW_LIMIT) + "..."}
        </div>

        {isLong && (
          <div className="text-center mt-2">
            <button
              className="lesson-read-more-btn"
              onClick={() => onToggle(!isExpanded)}
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          </div>
        )}
      </>
    );
  };

  const handleBackNavigation = () => {
    if (location.state?.fromAddCourse && id) {
      navigate(`${getCourseListingRoute(role)}/edit-course/${id}`);
      return;
    }

    navigate(getCourseListingRoute(role));
  };

  return (
    <>
      <AdminHeader />
      {isPreviewLoading && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Loading preview... Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            {isLoading.loader ? (
              <Card>
                <div className="loaderContainer custom-loader-height">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              </Card>
            ) : isLoading.error ? (
              <div className="loaderContainer error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{isLoading.error}</span>
              </div>
            ) : (
              <>
                <Card className="course-hero-card mb-4">
                  <CardBody className="card-body-custom">
                    <Row className="align-items-center">
                      {/* Left */}
                      <Col lg="8">
                        <div className="course-description-wrapper">
                          <div className="course-header-top">
                            <div className="course-header-left">
                              <button
                                className="td-back-btn"
                                onClick={handleBackNavigation}
                                title="Back"
                              >
                                <i className="fa fa-arrow-left" />
                              </button>

                              {courseData?.courseType?.value && (
                                <span
                                  className={`radio-badge ${
                                    courseData?.courseType?.value?.toLowerCase() ===
                                    "live"
                                      ? "live"
                                      : "recorded"
                                  }`}
                                >
                                  <span className="radio-circle">
                                    <span className="radio-dot" />
                                  </span>
                                  {courseData?.courseType?.value}
                                </span>
                              )}
                              
                              {(role === "SUPER_ADMIN" || role === "ADMIN") && (
                                <Badge
                                  color={getCourseStatusColor(
                                    courseData?.courseStatus?.value,
                                  )}
                                >
                                  {COURSE_STATUS_OPTIONS.find(
                                    (item) =>
                                      item.value ===
                                      courseData?.courseStatus?.value,
                                  )?.label || courseData?.courseStatus?.value}
                                </Badge>
                              )}
                            </div>
                          </div>

                          <h2 className="course-title mt-3 mb-3">
                            {courseData?.courseName?.value}
                          </h2>
                        </div>

                        {/* enrollment count + rating info */}
                        <div className="course-meta-summary d-flex justify-content-between align-items-center">
                          {/* Left */}
                          <div className="d-flex align-items-center">
                            <div className="meta-item mr-4">
                              {(courseData?.courseRating?.value || 0) > 0 ? (
                                <>
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <i
                                      key={star}
                                      className={`fa ${
                                        star <=
                                        Math.floor(
                                          courseData.courseRating.value,
                                        )
                                          ? "fa-star star-yellow"
                                          : star - 0.5 <=
                                              courseData.courseRating.value
                                            ? "fa-star-half-alt star-yellow"
                                            : "fa-star course-star-light"
                                      } mr-1`}
                                    />
                                  ))}

                                  <span className="ml-2 text-muted">
                                    ({courseData.courseRating.value})
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="ml-2 text-muted">
                                    No ratings yet
                                  </span>
                                </>
                              )}
                            </div>

                            <div className="meta-item mr-4">
                              <i className="fa fa-users btn1 mr-1" />
                              {courseData?.totalEnrollment?.value || 0} Enrolled
                            </div>

                            <div className="meta-item">
                              <i className="fa-solid fa-wallet btn1 mr-1"></i>

                              <span>
                                {courseData?.accessType?.value === "Free"
                                  ? "Free"
                                  : formattedPrice(courseData?.price?.value)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* course info bar  */}
                        <div className="course-info-bar">
                          {/* <div className="info-item">
                            <i className="fa fa-clock info-icon"></i>
                            <div>
                              <h4 className="text-default mb-0">
                                {courseData?.courseStartDate?.value}
                              </h4>
                              <p className="small-text mb-0">Duration</p>
                            </div>
                          </div> */}

                          <div className="info-item">
                            <i className="fa fa-users info-icon"></i>
                            <div>
                              <h4 className="text-default mb-0">
                                {courseData?.courseUsers?.value}
                              </h4>
                              <p className="small-text mb-0">Audience</p>
                            </div>
                          </div>

                          <div className="info-item">
                            <div className="info-icon letter-icon">
                              {courseData?.courseLanguage?.value === "ENGLISH"
                                ? "E"
                                : courseData?.courseLanguage?.value === "HINDI"
                                  ? "H"
                                  : "EH"}
                            </div>

                            <div>
                              <h4 className="text-default mb-0">
                                {formatText(courseData?.courseLanguage?.value)}
                              </h4>
                              <p className="small-text mb-0">Language</p>
                            </div>
                          </div>

                          <div className="info-item">
                            <i className="fa-solid fa-chart-simple info-icon"></i>
                            <div>
                              <h4 className="text-default mb-0">
                                {formatText(courseData?.level?.value)}
                              </h4>
                              <p className="small-text mb-0">Level</p>
                            </div>
                          </div>

                          <div className="info-item">
                            <i className="fa fa-book info-icon"></i>
                            <div>
                              <h4 className="text-default mb-0">
                                {totalLessons === 0 ? "No Lessons" : totalLessons}
                              </h4>
                              <p className="small-text mb-0">Lessons</p>
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Right */}
                      <Col lg="4">
                        <div className="course-image-section">
                          <div className="course-image-header">
                             {/* &&
                              Number(
                                courseData?.totalEnrollment?.value || 0,
                              ) === 0  */}
                            {activeTab === "details" &&
                              (role === "SUPER_ADMIN" || role === "ADMIN") && (
                                <Button
                                  color="priary"
                                  className="btn-deep-blue"
                                  onClick={() =>
                                    navigate(
                                      `${getCourseListingRoute(role)}/edit-course/${id}`,
                                      {
                                        state: { mode: "edit" },
                                      },
                                    )
                                  }
                                >
                                  <i className="fa fa-pencil mr-1" />
                                  Edit
                                </Button>
                              )}
                          </div>

                          <div
                            className="hero-image-wrapper cursor"
                            onClick={() => {
                              setIsPreviewLoading(true);
                              toggleThumbnailModal();
                            }}
                            title="Click to preview"
                          >
                            {isVideo ? (
                              <div className="video-thumbnail">
                                <video
                                  src={mediaUrl}
                                  className="hero-image"
                                  muted
                                  preload="metadata"
                                />
                                <div className="play-overlay">
                                  <i className="fa fa-play"></i>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={mediaUrl}
                                alt={courseData?.courseName?.value}
                                className="hero-image"
                              />
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* //tabs */}
                <Card className="main-tabs-card">
                  <CardHeader className="pb-0 course-sticky-header">
                    <Nav className="custom-tabs cursor">
                      <NavItem>
                        <NavLink
                          className={activeTab === "details" ? "active" : ""}
                          onClick={() => toggleTab("details")}
                        >
                          Course Details
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          className={activeTab === "batches" ? "active" : ""}
                          onClick={() => toggleTab("batches")}
                        >
                          Batches
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          className={activeTab === "assessment" ? "active" : ""}
                          onClick={() => toggleTab("assessment")}
                        >
                          Assessment
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          className={activeTab === "review-feedback" ? "active" : ""}
                          onClick={() => toggleTab("review-feedback")}
                        >
                          Review Feedback
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          className={`${activeTab === "enrollment" ? "active" : "disabled text-muted  not-allowed"}`}
                          onClick={(e) => e.preventDefault()}
                        >
                          Enrollment
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </CardHeader>

                  <CardBody>
                    <TabContent
                      activeTab={activeTab}
                      className="course-details"
                    >
                      <TabPane className="course-details" tabId="details">
                        <Row>
                          <Col md="8" className="d-flex flex-column">
                            <Card className="section-cards">
                              <CardHeader className="section-header">
                                <h4 className="text-default mb-0">
                                  About this Course
                                </h4>
                              </CardHeader>
                              <CardBody>
                                {renderExpandableDescription(
                                  courseData?.courseDescription?.value,
                                  isCourseDescExpanded,
                                  setIsCourseDescExpanded,
                                  "small-text course-content mb-0",
                                )}
                              </CardBody>
                            </Card>

                            <Card className="section-cards">
                              <CardHeader className="section-header">
                                <h4 className="text-default mb-0 d-flex align-items-center">
                                  Lessons
                                  {!hasLessonLanguageTabs && (
                                    <Badge color="primary" className="ml-2 px-2 py-1" pill>
                                      {displayedLessons?.length || 0}
                                    </Badge>
                                  )}
                                </h4>
                              </CardHeader>
                              <CardBody>
                                {hasLessonLanguageTabs && (
                                  <Nav tabs className="mb-3">
                                    <NavItem>
                                      <NavLink
                                        className={`cursor ${
                                          activeLessonLanguage === "ENGLISH"
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          setActiveLessonLanguage("ENGLISH")
                                        }
                                      >
                                        English
                                        <Badge color={activeLessonLanguage === "ENGLISH" ? "primary" : "secondary"} className="ml-2" pill>
                                          {courseData.lessonByLanguage?.englishLessons?.length || 0}
                                        </Badge>
                                      </NavLink>
                                    </NavItem>

                                    <NavItem>
                                      <NavLink
                                        className={`cursor ${
                                          activeLessonLanguage === "HINDI"
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          setActiveLessonLanguage("HINDI")
                                        }
                                      >
                                        Hindi
                                        <Badge color={activeLessonLanguage === "HINDI" ? "primary" : "secondary"} className="ml-2" pill>
                                          {courseData.lessonByLanguage?.hindiLessons?.length || 0}
                                        </Badge>
                                      </NavLink>
                                    </NavItem>
                                  </Nav>
                                )}
                                {displayedLessons.length === 0 ? (
                                  <p className="text-muted mb-0">
                                    No lessons added
                                  </p>
                                ) : (
                                  displayedLessons.map((lesson, index) => (
                                    <Card
                                      key={lesson.id || index}
                                      className="mb-2 border"
                                    >
                                      <CardHeader>
                                        <SectionHeader
                                          title={
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                              <span className="configurations-header-title">
                                                {lesson.lessonSeqNo}.{" "}
                                                {lesson.lessonName}
                                              </span>

                                              {lesson.documents?.length > 0 && (
                                                <>
                                                  <i
                                                    id={`courseDetailLessonIcon-${lesson.id}`}
                                                    className={`fa ${getLessonIcon(
                                                      lesson,
                                                    )} lessonIcon  ml-2 cursor`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();

                                                      const fileUrl =
                                                        lesson.documents[0]
                                                          .file_path;

                                                      setSelectedLessonDocument(
                                                        {
                                                          url: fileUrl,
                                                          type: fileUrl
                                                            .split(".")
                                                            .pop()
                                                            ?.toLowerCase(),
                                                        },
                                                      );
                                                      setIsPreviewLoading(true);
                                                      setIsLessonDocumentModalOpen(
                                                        true,
                                                      );
                                                    }}
                                                  />

                                                  <UncontrolledPopover
                                                    placement="top"
                                                    target={`courseDetailLessonIcon-${lesson.id}`}
                                                    trigger="hover"
                                                    className="shadow-lg border-0"
                                                    container="body"
                                                  >
                                                    <PopoverBody>
                                                      Click icon to preview
                                                      lesson
                                                    </PopoverBody>
                                                  </UncontrolledPopover>
                                                </>
                                              )}
                                            </div>
                                          }
                                          isOpen={openLessonIndex === index}
                                          handleOnClick={() =>
                                            setOpenLessonIndex(
                                              openLessonIndex === index
                                                ? null
                                                : index,
                                            )
                                          }
                                        />
                                      </CardHeader>
                                      <Collapse
                                        isOpen={openLessonIndex === index}
                                      >
                                        <CardBody>
                                          <div className="d-flex align-items-center mb-2">
                                            <i className="fa-regular fa-clock mr-2 text-muted" />
                                            <span className="mr-1">
                                              Duration:
                                            </span>
                                            <strong>{lesson.duration}</strong>
                                          </div>
                                          <div className="d-flex align-items-center mb-2">
                                            <i className="fa fa-layer-group mr-2 text-muted" />
                                            <span className="mr-2">Type:</span>
                                            <Badge
                                              color={
                                                LessonTypeColor[lesson.type]
                                              }
                                            >
                                              {lesson.type}
                                            </Badge>
                                          </div>
                                          {lesson.description &&
                                            renderExpandableDescription(
                                              lesson.description,
                                              !!expandedDescriptions[lesson.id],
                                              (value) =>
                                                setExpandedDescriptions(
                                                  (prev) => ({
                                                    ...prev,
                                                    [lesson.id]: value,
                                                  }),
                                                ),
                                              "mt-2 course-content mb-0",
                                            )}
                                        </CardBody>
                                      </Collapse>
                                    </Card>
                                  ))
                                )}
                              </CardBody>
                            </Card>
                          </Col>

                          <Col md="4">
                            {courseData.preRequisites.value === "YES" &&
                            courseData.requiredDoc?.value?.length > 0 && (
                              <Card className="section-cards">
                                <CardHeader className="section-header">
                                  <h4 className="text-default mb-0">
                                    Prerequisites
                                  </h4>
                                </CardHeader>

                                <CardBody>
                                  <ul className="desc-text pl-3 mb-0">
                                    {courseData.requiredDoc.value.map((doc) => (
                                      <li key={doc.value}>{doc.label}</li>
                                    ))}
                                  </ul>
                                </CardBody>
                              </Card>
                            )}

                            <Card className="section-cards">
                              <CardHeader className="section-header">
                                <h4 className="text-default mb-0">
                                  Configurations
                                </h4>
                              </CardHeader>

                              <CardBody className="pb-0">
                                {hasConfigurations ? (
                                  <Row className="text-center mb-0">
                                    {courseData.accessType?.value && (
                                      <Col md="4">
                                        <div className="config-item">
                                          <div className="config-icon">
                                            <i className="fa fa-lock" />
                                          </div>
                                          <span className="configurations-header-title text-nowrap">
                                            {courseData.accessType?.value}
                                          </span>
                                          <Label className="small-label text-nowrap">
                                            Access Type
                                          </Label>
                                        </div>
                                      </Col>
                                    )}

                                    {courseData.courseUsers?.value && (
                                      <Col md="4">
                                        <div className="config-item">
                                          <div className="config-icon">
                                            <i className="fa fa-users" />
                                          </div>
                                          <span className="configurations-header-title">
                                            {courseData.courseUsers?.value}
                                          </span>
                                          <Label className="small-label text-nowrap">
                                            Participant Type
                                          </Label>
                                        </div>
                                      </Col>
                                    )}

                                    {isLiveCourse &&
                                      courseData.contentDeliveryMode?.value && (
                                        <Col md="4">
                                          <div className="config-item">
                                            <div className="config-icon">
                                              <i className="fa-solid fa-chalkboard-user" />
                                            </div>
                                            <span className="configurations-header-title">
                                              {
                                                courseData.contentDeliveryMode
                                                  ?.value
                                              }
                                            </span>
                                            <Label className="small-label text-nowrap">
                                              Delivery Mode
                                            </Label>
                                          </div>
                                        </Col>
                                      )}

                                    {/* {isOfflineOrHybrid &&
                                      courseData.assessmentType?.value && (
                                        <Col md="4">
                                          <div className="config-item">
                                            <div className="config-icon">
                                              <i className="fa fa-clipboard-check" />
                                            </div>
                                            <span className="configurations-header-title">
                                              {courseData.assessmentType?.value}
                                            </span>
                                            <Label className="small-label">
                                              Assessment
                                            </Label>
                                          </div>
                                        </Col>
                                      )} */}
                                  </Row>
                                ) : (
                                  <p className="text-muted mb-0">
                                    No configurations found
                                  </p>
                                )}
                              </CardBody>
                            </Card>
                          {role !== "TRAINER" && (() => {
                                const templates = courseData.certificateDetails?.value || [];
                                const compTemplateObj = templates.find((t) => t.certificateType === "COMPLETION");
                                const partTemplateObj = templates.find((t) => t.certificateType === "PARTICIPATION");
                                
                                if (!compTemplateObj && !partTemplateObj) {
                                  return null;
                                }

                                const hasBoth = compTemplateObj && partTemplateObj;
                                const headingText = hasBoth
                                  ? "Certificate"
                                  : compTemplateObj
                                  ? "Completion Certificate"
                                  : partTemplateObj
                                  ? "Participation Certificate"
                                  : "Certificate";

                              const renderCertPreview = (templateStr) => {
                                if (!templateStr) return null;
                                try {
                                  const templateJson = mapBackendTemplateToFrontend({ template: templateStr });
                                  return (
                                    <div
                                      className="certificate-preview-frame certificate-clickable cursor"
                                      onClick={toggleCertificateModal}
                                      style={{ width: "100%" }}
                                    >
                                      <ThumbnailPreview parsedTemplate={templateJson} />
                                    </div>
                                  );
                                } catch (err) {
                                  return <div className="text-danger">Error loading certificate template.</div>;
                                }
                              };

                              const formatValidity = (template) => {
                                if (!template) return null;
                                if (!template.durationValue || !template.durationType) return "Validity: No Expiry";
                                const val = template.durationValue;
                                const type = toTitleCase((template.durationType || "").replace(/s$/i, ""));
                                return `Validity: ${val} ${type}${val > 1 ? "s" : ""}`;
                              };

                              return (
                                <Card className="section-cards">
                                  {hasBoth ? (
                                    <>
                                      <CardHeader className="section-header">
                                        <h4 className="text-default mb-0">Certificate</h4>
                                      </CardHeader>
                                      <CardBody>
                                        <Nav tabs className="mb-3 certificate-tabs">
                                          <NavItem>
                                            <NavLink
                                              className={activeCertTab === "completion" ? "active" : ""}
                                              onClick={() => setActiveCertTab("completion")}
                                              style={{ cursor: "pointer" }}
                                            >
                                              Completion
                                            </NavLink>
                                          </NavItem>
                                          <NavItem>
                                            <NavLink
                                              className={activeCertTab === "participation" ? "active" : ""}
                                              onClick={() => setActiveCertTab("participation")}
                                              style={{ cursor: "pointer" }}
                                            >
                                              Participation
                                            </NavLink>
                                          </NavItem>
                                        </Nav>
                                        <div className="certificate-side-preview" ref={setPreviewRef}>
                                          {activeCertTab === "completion" && (
                                            <>
                                              {renderCertPreview(compTemplateObj?.certificateTemplate)}
                                              {formatValidity(compTemplateObj) && (
                                                <p className="text-muted mt-2 text-center">
                                                  <strong>{formatValidity(compTemplateObj)}</strong>
                                                </p>
                                              )}
                                            </>
                                          )}
                                          {activeCertTab === "participation" && renderCertPreview(partTemplateObj?.certificateTemplate)}
                                        </div>
                                      </CardBody>
                                    </>
                                  ) : (
                                    <>
                                      <CardHeader className="section-header">
                                        <h4 className="text-default mb-0">{headingText}</h4>
                                      </CardHeader>
                                      <CardBody>
                                        <div className="certificate-side-preview" ref={setPreviewRef}>
                                          {renderCertPreview((compTemplateObj || partTemplateObj)?.certificateTemplate)}
                                          {compTemplateObj && formatValidity(compTemplateObj) && (
                                            <p className="text-muted mt-2 text-center">
                                              <strong>{formatValidity(compTemplateObj)}</strong>
                                            </p>
                                          )}
                                        </div>
                                      </CardBody>
                                    </>
                                  )}
                                </Card>
                              );
                            })()}
                          </Col>
                        </Row>
                      </TabPane>

                      <TabPane tabId="batches">
                        {activeTab === "batches" && (
                          <CourseSchedule courseId={decodedId} deliveryMode={courseData.contentDeliveryMode?.value} courseData={courseData}/>
                        )}
                      </TabPane>

                      <TabPane tabId="assessment">
                        {activeTab === "assessment" && (
                          <Assessment 
                            courseId={decodedId} 
                            isOffline={isLiveCourse && isOffline}
                            isHybrid={isLiveCourse && courseData.contentDeliveryMode?.value === "Online, Offline"} 
                          />
                        )}
                      </TabPane>

                      <TabPane tabId="review-feedback">
                        {activeTab === "review-feedback" && (
                          <ReviewFeedback courseId={decodedId} />
                        )}
                      </TabPane>

                      <TabPane tabId="enrollment">
                        {activeTab === "enrollment" && (
                          <EnrolledStudentsTab courseID={decodedId} courseName={courseData?.courseName?.value} accessType={courseData?.accessType?.value} />
                        )}
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>
      <Modal
        isOpen={isCertificateModalOpen}
        toggle={toggleCertificateModal}
        size="xl"
        centered
        className="certificate-preview-modal"
      >
        <ModalHeader toggle={toggleCertificateModal}>
          Certificate Preview
        </ModalHeader>

        <ModalBody className="certificate-preview-modal-body">
          {(() => {
            const templates = courseData.certificateDetails?.value || [];
            const compTemplateObj = templates.find((t) => t.certificateType === "COMPLETION");
            const partTemplateObj = templates.find((t) => t.certificateType === "PARTICIPATION");
            const hasBoth = compTemplateObj && partTemplateObj;

            // Pick whichever template is currently active
            let activeTemplateStr = null;
            if (hasBoth) {
              activeTemplateStr = activeCertTab === "completion"
                ? compTemplateObj?.certificateTemplate
                : partTemplateObj?.certificateTemplate;
            } else {
              activeTemplateStr = compTemplateObj?.certificateTemplate || partTemplateObj?.certificateTemplate;
            }

            if (!activeTemplateStr) {
              return (
                <div className="text-center p-5 text-muted">
                  No certificate added.
                </div>
              );
            }

            try {
              // const templateJson = JSON.parse(activeTemplateStr);
              return (
                <div
                  className="template-preview-wrapper mx-auto"
                  ref={setModalRef}
                >
                  <div className="certificate-json-preview">
                    <ThumbnailPreview parsedTemplate={mapBackendTemplateToFrontend({ template: activeTemplateStr })} />
                  </div>
                </div>
              );
            } catch (err) {
              return (
                <div className="text-center text-danger">
                  Error rendering certificate template.
                </div>
              );
            }
          })()}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={isLessonDocumentModalOpen}
        toggle={toggleLessonDocumentModal}
        size="xl"
        centered
        className="certificate-preview-modal"
      >
        <ModalHeader toggle={toggleLessonDocumentModal}>
          Lesson Document Preview
        </ModalHeader>

        <ModalBody className="certificate-preview-modal-body">
          {["jpg", "jpeg", "png", "gif", "webp", "svg", "avif"].includes(
            selectedLessonDocument?.type,
          ) && (
            <img
              src={selectedLessonDocument.url}
              alt="Lesson Document"
              className="certificate-preview-modal-image"
              onLoad={() => setIsPreviewLoading(false)}
              onError={() => setIsPreviewLoading(false)}
            />
          )}

          {["mp4", "webm", "ogg", "mov", "m4v"].includes(
            selectedLessonDocument?.type,
          ) && (
            <video
              controls
              autoPlay
              className="w-100 lesson-preview-video"
              onLoadedData={() => setIsPreviewLoading(false)}
              onError={() => setIsPreviewLoading(false)}
            >
              <source src={selectedLessonDocument.url} />
            </video>
          )}

          {["pdf"].includes(selectedLessonDocument?.type) && (
            <iframe
              src={selectedLessonDocument.url}
              title="PDF Preview"
              width="100%"
              height="37.5rem"
              onLoad={() => setIsPreviewLoading(false)}
            />
          )}

          {["doc", "docx", "xls", "xlsx"].includes(
            selectedLessonDocument?.type,
          ) && (
            <iframe
              src={`${OFFICE_PREVIEW_URL}${encodeURIComponent(
                selectedLessonDocument.url,
              )}`}
              title="Document Preview"
              width="100%"
              height="100%"
              onLoad={() => setIsPreviewLoading(false)}
            />
          )}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={isThumbnailModalOpen}
        toggle={toggleThumbnailModal}
        size="xl"
        centered
        className="certificate-preview-modal"
      >
        <ModalHeader toggle={toggleThumbnailModal}>
          Thumbnail Preview
        </ModalHeader>
        <ModalBody className="certificate-preview-modal-body">
          {isVideo ? (
            <video
              src={courseData.courseMedia?.value}
              controls
              autoPlay
              className="w-100"
              onLoadedData={() => setIsPreviewLoading(false)}
              onError={() => setIsPreviewLoading(false)}
            />
          ) : (
            <img
              src={courseData.courseMedia?.value}
              alt="Thumbnail Preview"
              className="certificate-preview-modal-image"
              onLoad={() => setIsPreviewLoading(false)}
              onError={() => setIsPreviewLoading(false)}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default CourseDetail;
